import { z } from 'zod';

export const WORKFLOW_SPEC_SCHEMA_VERSION = 7;

export const RESERVED_REASON_IDS = [
  'automaticallyCompleted',
  'automaticallyCancelled',
  'manuallyCompleted',
  'manuallyCancelled',
] as const;

const kebabCaseString = z
  .string()
  .min(1)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'must be kebab-case (lowercase, hyphen-separated)');

const camelCaseString = z
  .string()
  .min(1)
  .regex(/^[a-z][a-zA-Z0-9]*$/, 'must be camelCase');

const snakeCaseReasonId = z
  .string()
  .min(1)
  .regex(/^[a-z][a-z0-9_]*$/, 'must be snake_case')
  .refine(
    (v) => !(RESERVED_REASON_IDS as readonly string[]).includes(v),
    'is a reserved reason ID — pick a different name',
  );

const ManualGateSchema = z.discriminatedUnion('allowed', [
  z.object({ allowed: z.literal('always') }),
  z.object({ allowed: z.literal('never') }),
  z.object({
    allowed: z.literal('conditional'),
    conditionPlainEnglish: z.string().min(1),
  }),
]);
export type ManualGate = z.infer<typeof ManualGateSchema>;

const ManualClosureReasonSchema = z.object({
  label: z.string().min(1),
  reasonId: snakeCaseReasonId,
  resolvesAs: z.enum(['completed', 'cancelled']),
});
export type ManualClosureReason = z.infer<typeof ManualClosureReasonSchema>;

const AutoResolutionRuleSchema = z.object({
  conditionPlainEnglish: z.string().min(1),
  reasonId: snakeCaseReasonId,
  // The audit-log description is derived from the condition during
  // translation (Claude distills the plain-English condition into a short
  // label and shows it in the plan). Stored as an empty string in the
  // exported spec.
  description: z.string(),
});
export type AutoResolutionRule = z.infer<typeof AutoResolutionRuleSchema>;

const StepRefSchema = z.object({ stepId: kebabCaseString });
export type StepRef = z.infer<typeof StepRefSchema>;

const FormValueGateSchema = z.object({
  fromStepId: kebabCaseString,
  fieldKey: camelCaseString,
  value: z.string().min(1),
});
export type FormValueGate = z.infer<typeof FormValueGateSchema>;

const CompletionFormValueGateSchema = z.object({
  fieldKey: camelCaseString,
  value: z.string().min(1),
});
export type CompletionFormValueGate = z.infer<typeof CompletionFormValueGateSchema>;

const DueSpecSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('never') }),
  z.object({
    kind: z.literal('afterDuration'),
    iso8601Duration: z.string().min(2),
    timeOfDay: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('expression'),
    descriptionPlainEnglish: z.string().min(1),
  }),
]);
export type DueSpec = z.infer<typeof DueSpecSchema>;

const RetrySpecSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('never') }),
  z.object({
    kind: z.literal('afterDuration'),
    iso8601Duration: z.string().min(2),
    timeOfDay: z.string().nullable(),
    maxAttempts: z.number().int().positive().nullable(),
  }),
  z.object({
    kind: z.literal('afterDurationExpression'),
    descriptionPlainEnglish: z.string().min(1),
    maxAttempts: z.number().int().positive().nullable(),
  }),
  z.object({
    kind: z.literal('onDateExpression'),
    descriptionPlainEnglish: z.string().min(1),
    maxAttempts: z.number().int().positive().nullable(),
  }),
]);
export type RetrySpec = z.infer<typeof RetrySpecSchema>;

const AssignmentSpecSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('user'), userId: z.string().min(1) }),
  z.object({ kind: z.literal('loanPointOfContact') }),
  z.object({
    kind: z.literal('team'),
    teamId: z.string().min(1),
    choose: z.enum(['first', 'random']),
  }),
]);
export type AssignmentSpec = z.infer<typeof AssignmentSpecSchema>;

const VariantConfigSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('generic') }),
  z.object({
    kind: z.literal('phoneCall'),
    recipient: z.enum(['borrower', 'loanPointOfContact']),
    script: z.string().min(1),
  }),
  z.object({
    kind: z.literal('sendLetter'),
    recipient: z.literal('borrower'),
    contents: z.string().min(1),
  }),
]);
export type VariantConfig = z.infer<typeof VariantConfigSchema>;

const NonGroupElementConfigSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('textInput'), validValues: z.array(z.string()).nullable() }),
  z.object({ kind: z.literal('textArea'), validValues: z.array(z.string()).nullable() }),
  z.object({
    kind: z.literal('select'),
    validValues: z.array(z.string()),
    typeaheadSearch: z.boolean(),
  }),
  z.object({
    kind: z.literal('radio'),
    validValues: z.array(z.string()),
  }),
  z.object({
    kind: z.literal('boolean'),
  }),
  z.object({
    kind: z.literal('numberInput'),
    min: z.number().nullable(),
    max: z.number().nullable(),
  }),
  z.object({
    kind: z.literal('currencyInput'),
    min: z.number().nullable(),
    max: z.number().nullable(),
  }),
  z.object({ kind: z.literal('email') }),
  z.object({ kind: z.literal('phoneNumber') }),
  z.object({ kind: z.literal('plainDate') }),
  z.object({ kind: z.literal('borrowerSelect') }),
  z.object({ kind: z.literal('presentationOnlyMarkdown'), markdownContent: z.string().min(1) }),
]);

type NonGroupElementConfig = z.infer<typeof NonGroupElementConfigSchema>;

// Recursive: a 'group' element contains children of FormFieldSpec (which itself can hold another group).
// Define explicit TS types and use z.lazy for the recursion.
export type FormElementConfig = NonGroupElementConfig | { kind: 'group'; children: FormFieldSpec[] };

export type FormFieldSpec = {
  key: string;
  label: string;
  required: boolean;
  multiValued: boolean;
  element: FormElementConfig;
};

const FormFieldSpecSchema: z.ZodType<FormFieldSpec> = z.lazy(() =>
  z.object({
    key: camelCaseString,
    label: z.string().min(1),
    required: z.boolean(),
    multiValued: z.boolean(),
    element: z.union([
      NonGroupElementConfigSchema,
      z.object({
        kind: z.literal('group'),
        children: z.array(FormFieldSpecSchema).min(1),
      }),
    ]),
  }),
);

const DisplayInfoBlockSchema = z.object({
  markdownContent: z.string().min(1),
  visibleWhenPlainEnglish: z.string().min(1).nullable(),
});
export type DisplayInfoBlock = z.infer<typeof DisplayInfoBlockSchema>;

// Action descriptions are intentionally free-form plain English — the engineer
// translates each description into the appropriate Action / OnEventAction
// TypeScript type when implementing the workflow.
const ActionDescriptionSpecSchema = z.object({
  descriptionPlainEnglish: z.string().min(1),
  conditionPlainEnglish: z.string().min(1).nullable(),
});
export type ActionDescriptionSpec = z.infer<typeof ActionDescriptionSpecSchema>;
// Legacy aliases — same shape now, kept for callers that referenced the old types.
export type OnEventActionSpec = ActionDescriptionSpec;
export type InlineActionSpec = ActionDescriptionSpec;

const CreateTaskSpecSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('always') }),
  z.object({ kind: z.literal('never') }),
  z.object({ kind: z.literal('conditional'), conditionPlainEnglish: z.string().min(1) }),
]);
export type CreateTaskSpec = z.infer<typeof CreateTaskSpecSchema>;

const StepSpecSchema = z.object({
  id: kebabCaseString,
  title: z.string().min(1),
  description: z.string(),
  variant: z.enum(['generic', 'phoneCall', 'sendLetter']),
  variantConfig: VariantConfigSchema,
  assignment: AssignmentSpecSchema,
  activation: z.object({
    autoActivate: z.boolean(),
    triggeredBy: z.array(StepRefSchema),
    triggerCombinator: z.enum(['all', 'any']),
    formValueGates: z.array(FormValueGateSchema),
    additionalConditionPlainEnglish: z.string().min(1).nullable(),
    manualActivation: ManualGateSchema,
  }),
  completion: z.object({
    autoCompleteOnFormSubmit: z.boolean(),
    autoCompletePlainEnglish: z.string(),
    formValueGates: z.array(CompletionFormValueGateSchema),
    manualCompletion: ManualGateSchema,
  }),
  skip: z.object({
    autoSkipPlainEnglish: z.string().min(1).nullable(),
    manualSkip: ManualGateSchema,
  }),
  fail: z.object({
    autoFailPlainEnglish: z.string().min(1).nullable(),
    manualFail: ManualGateSchema,
  }),
  visibility: z.object({
    isVisiblePlainEnglish: z.string().min(1).nullable(),
  }),
  due: DueSpecSchema,
  retry: RetrySpecSchema,
  dataCollection: z.array(FormFieldSpecSchema),
  displayInfo: z.array(DisplayInfoBlockSchema),
  onActivateActions: z.array(ActionDescriptionSpecSchema),
  onCompleteActions: z.array(ActionDescriptionSpecSchema),
  inlineActions: z.array(ActionDescriptionSpecSchema),
  createTask: CreateTaskSpecSchema,
  editAfterCompletion: ManualGateSchema,
  supportNotes: z.boolean(),
});
export type StepSpec = z.infer<typeof StepSpecSchema>;

export const WorkflowSpecSchema = z.object({
  schemaVersion: z.literal(WORKFLOW_SPEC_SCHEMA_VERSION),
  meta: z.object({
    title: z.string().min(1),
    description: z.string(),
    workflowId: kebabCaseString,
    version: z.number().int().nonnegative(),
    tags: z.array(z.string().min(1)),
  }),
  instantiation: z.object({
    triggerDescription: z.string(),
    allowConcurrentInstances: z.boolean(),
  }),
  resolution: z.object({
    autoComplete: z.array(AutoResolutionRuleSchema),
    autoCancel: z.array(AutoResolutionRuleSchema),
    manualComplete: ManualGateSchema,
    manualCancel: ManualGateSchema,
    manualClosureReasons: z.array(ManualClosureReasonSchema),
  }),
  steps: z.array(StepSpecSchema),
});

export type WorkflowSpec = z.infer<typeof WorkflowSpecSchema>;

export {
  ManualGateSchema,
  ManualClosureReasonSchema,
  AutoResolutionRuleSchema,
  StepRefSchema,
  FormValueGateSchema,
  CompletionFormValueGateSchema,
  DueSpecSchema,
  RetrySpecSchema,
  AssignmentSpecSchema,
  VariantConfigSchema,
  FormFieldSpecSchema,
  DisplayInfoBlockSchema,
  ActionDescriptionSpecSchema,
  CreateTaskSpecSchema,
  StepSpecSchema,
};
